const BASE62_RADIX = 62n;
const BASE62_ALPHABET =
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

/**
 * Encodes a non-negative BigInt into a **fixed-length base62** string.
 *
 * Base62 alphabet:
 * - We use the standard base62 alphabet (0-9, A-Z, a-z).
 *
 * Why fixed length?
 * - Many IDs we work with have a strict length requirement (e.g. 22 chars for short IDs).
 * - Base62 encoding is variable-length by default (leading zeros disappear), so we left-pad with `'0'`
 *   to ensure we always return exactly `length` characters.
 *
 * Overflow behaviour:
 * - If the base62 representation would be longer than `length`, we throw (to avoid silently truncating
 *   and causing collisions).
 *
 * Import use-case:
 * - For deterministic domainEntityId remapping, we encode a 128-bit hash into 22 base62 chars.
 * - 22 base62 chars can represent \(62^{22}\) possibilities (~131 bits), so 128-bit inputs will not overflow.
 *
 * Contract:
 * - Throws if `length` is not a positive integer
 * - Returns a string of exactly `length` characters
 * - Throws if the encoded value would exceed `length` characters
 */
export const encodeBigIntToBase62FixedLength = (
  value: bigint,
  length: number,
): string => {
  if (length <= 0) {
    throw new Error(
      'encodeBigIntToBase62FixedLength: length must be a positive integer',
    );
  }
  if (value < 0n) {
    throw new Error(
      'encodeBigIntToBase62FixedLength: value must be non-negative',
    );
  }
  if (value === 0n) return '0'.padStart(length, '0');

  let v = value;
  let out = '';
  while (v > 0n) {
    // Base62 "digits": repeatedly take the remainder mod 62 and prepend the mapped character.
    const rem = v % BASE62_RADIX;
    out = BASE62_ALPHABET[Number(rem)] + out;
    v /= BASE62_RADIX;
  }

  if (out.length > length) {
    throw new Error(
      `encodeBigIntToBase62FixedLength: encoded length ${out.length} exceeds fixed length ${length}`,
    );
  }

  // Ensure stable fixed width even when the base62 representation has fewer digits.
  return out.padStart(length, '0');
};
